import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Package, ShoppingCart } from 'lucide-react';

const Suministros = () => {
  const [suministros, setSuministros] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('catalogo');
  const [formData, setFormData] = useState({ nombre: '', descripcion: '', categoria: 'cajas', unidad: 'unidad', precio_compra: 0, precio_venta: 0, stock_actual: 0, stock_minimo: 10 });

  useEffect(() => {
    fetchSuministros();
    fetchVentas();
  }, []);

  const fetchSuministros = async () => {
    try {
      const response = await api.get('/suministros/catalogo');
      setSuministros(response.data || []);
    } catch (error) {
      toast.error('Error al cargar suministros');
    } finally {
      setLoading(false);
    }
  };

  const fetchVentas = async () => {
    try {
      const response = await api.get('/suministros/ventas-suministro');
      setVentas(response.data || []);
    } catch (error) {
      console.error('Error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/suministros/catalogo', formData);
      toast.success('Suministro creado');
      fetchSuministros();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl flex items-center"><Package className="mr-2" />Suministros</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Nuevo Suministro</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuevo Suministro</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} required /></div>
                    <div><Label>Categoría</Label>
                      <Select value={formData.categoria} onValueChange={(value) => setFormData({...formData, categoria: value})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cajas">Cajas</SelectItem><SelectItem value="cintas">Cintas</SelectItem><SelectItem value="embalaje">Embalaje</SelectItem><SelectItem value="etiquetas">Etiquetas</SelectItem><SelectItem value="otros">Otros</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Precio Compra</Label><Input type="number" step="0.01" value={formData.precio_compra} onChange={(e) => setFormData({...formData, precio_compra: parseFloat(e.target.value) || 0})} /></div>
                      <div><Label>Precio Venta</Label><Input type="number" step="0.01" value={formData.precio_venta} onChange={(e) => setFormData({...formData, precio_venta: parseFloat(e.target.value) || 0})} /></div>
                      <div><Label>Stock Actual</Label><Input type="number" value={formData.stock_actual} onChange={(e) => setFormData({...formData, stock_actual: parseInt(e.target.value) || 0})} /></div>
                      <div><Label>Stock Mínimo</Label><Input type="number" value={formData.stock_minimo} onChange={(e) => setFormData({...formData, stock_minimo: parseInt(e.target.value) || 0})} /></div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit">Crear</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList><TabsTrigger value="catalogo">Catálogo</TabsTrigger><TabsTrigger value="ventas">Ventas</TabsTrigger></TabsList>
              <TabsContent value="catalogo">
                {loading ? <div className="text-center py-8">Cargando...</div> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Categoría</TableHead><TableHead>Precio Venta</TableHead><TableHead>Stock</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {suministros.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center">No hay suministros</TableCell></TableRow> : suministros.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.nombre}</TableCell>
                          <TableCell>{item.categoria}</TableCell>
                          <TableCell>${item.precio_venta?.toFixed(2)}</TableCell>
                          <TableCell className={item.stock_actual < item.stock_minimo ? 'text-red-600 font-semibold' : ''}>{item.stock_actual} {item.unidad}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
              <TabsContent value="ventas">
                <Table>
                  <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Total</TableHead><TableHead>Método Pago</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {ventas.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center">No hay ventas</TableCell></TableRow> : ventas.map((venta) => (
                      <TableRow key={venta.id}>
                        <TableCell>{venta.fecha}</TableCell>
                        <TableCell className="font-semibold">${venta.total?.toFixed(2)}</TableCell>
                        <TableCell>{venta.metodo_pago}</TableCell>
                        <TableCell>{venta.estado_pago}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Suministros;
